import React from 'react';
import { Link } from 'react-router-dom';
import { getSeverityColor, formatRelativeTime } from '../../services/commandCenterService';
import './TopRisksTable.css';

/**
 * TopRisksTable - Table showing top risks across all assets
 * PRD-67 §4.3: three-class empty-state taxonomy keyed off `meta`.
 * @param {object} props - { risks, limit, meta }
 */
function TopRisksTable({ risks = [], limit = 10, title = 'Top Risks Across All Assets', meta }) {
    const displayedRisks = risks.slice(0, limit);

    // PRD-67 §4.3: resolve empty-state class from meta diagnostics.
    // Order matters — pick the first matching class.
    const resolveEmptyClass = () => {
        if (!meta) {
            // No meta yet (older payload or transient) — fall back to legacy generic.
            return 'generic';
        }
        const assetsConsidered = meta.assetsConsidered ?? 0;
        const findingsLoaded = meta.findingsLoaded ?? 0;
        if (assetsConsidered === 0 || findingsLoaded === 0) return 'no-data';
        // Scans evaluated, but no findings reached the widget's filter.
        return 'below-threshold';
    };

    const renderEmptyState = () => {
        const klass = resolveEmptyClass();
        if (klass === 'no-data') {
            return (
                <div className="table-empty">
                    <p>Run your first scan to see top risks.</p>
                    <p className="empty-hint">
                        <Link to="/assets" className="empty-link">Go to Asset Management</Link>
                    </p>
                </div>
            );
        }
        if (klass === 'below-threshold') {
            const evaluated = meta?.findingsLoaded ?? 0;
            const failures = meta?.blobLoadFailures ?? 0;
            return (
                <div className="table-empty">
                    <p>
                        {evaluated} scan{evaluated === 1 ? '' : 's'} evaluated, none reached
                        Critical or High severity.
                    </p>
                    {failures > 0 && (
                        <p className="empty-hint">
                            {failures} scan result{failures === 1 ? '' : 's'} could not be loaded.
                        </p>
                    )}
                </div>
            );
        }
        return (
            <div className="table-empty">
                <p>No high-priority risks found</p>
                <p className="empty-hint">Your assets appear to be in good health</p>
            </div>
        );
    };

    // Get priority badge class
    const getPriorityClass = (priority) => {
        const classes = {
            CRITICAL: 'priority-critical',
            HIGH: 'priority-high',
            MEDIUM: 'priority-medium',
            LOW: 'priority-low',
            INFO: 'priority-info'
        };
        return classes[priority] || 'priority-medium';
    };

    // Truncate description
    const truncateDescription = (desc, maxLength = 80) => {
        if (!desc) return 'No description available';
        if (desc.length <= maxLength) return desc;
        return desc.substring(0, maxLength) + '...';
    };

    return (
        <div className="top-risks-table">
            <h3 className="table-title">{title}</h3>

            {displayedRisks.length === 0 ? renderEmptyState() : (
                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>Asset</th>
                                <th>Vulnerability</th>
                                <th>Severity</th>
                                <th>Priority</th>
                                <th>First Seen</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedRisks.map((risk, index) => (
                                <tr key={`${risk.assetId}-${risk.finding}-${index}`}>
                                    <td className="cell-asset">
                                        <Link to={`/assets/${risk.assetId}`} className="asset-link">
                                            {risk.assetValue || risk.host}
                                        </Link>
                                        {risk.affectedCount > 1 && (
                                            <span className="risk-multi-asset" title={`Affects ${risk.affectedAssets?.join(', ')}`}>
                                                {risk.affectedCount} assets
                                            </span>
                                        )}
                                    </td>
                                    <td className="cell-finding">
                                        <div className="finding-name">{risk.finding}</div>
                                        <div className="finding-desc">
                                            {truncateDescription(risk.description)}
                                        </div>
                                    </td>
                                    <td className="cell-severity">
                                        <span
                                            className="severity-badge"
                                            style={{ color: getSeverityColor(risk.severity) }}
                                        >
                                            {risk.severity?.toUpperCase() || 'UNKNOWN'}
                                        </span>
                                    </td>
                                    <td className="cell-priority">
                                        <span className={`priority-badge ${getPriorityClass(risk.priority)}`}>
                                            {risk.priority}
                                        </span>
                                    </td>
                                    <td className="cell-date">
                                        {formatRelativeTime(risk.firstSeen)}
                                    </td>
                                    <td className="cell-action">
                                        <Link
                                            to={`/assets/${risk.assetId}`}
                                            className="view-btn"
                                            title="View asset details"
                                        >
                                            →
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default TopRisksTable;
